// 3rd party
import { Component, OnInit, ElementRef, Renderer2, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/Rx';
import { MatSidenav } from '@angular/material';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { AppComponent } from './../../app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { ContactCustomer, ERoleActionWebview } from 'app/shared/models/contact-customer.model';
import { StoredFile } from 'app/shared/models/stored-file.model';

// services
import { StandardService } from 'app/standards/shared/standard.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';

@Component({
  selector: 'app-standard-calibration-list',
  templateUrl: './standard-calibration-list.component.html',
  styleUrls: ['./standard-calibration-list.component.css']
})
export class StandardCalibrationListComponent implements OnInit, OnDestroy {

  @ViewChild('sidenavRight') sidenavRight: MatSidenav;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  cancelSidenavOpen: boolean = false;
  checkedItems: number = 0;
  checkedMenuItems: string[] = [];
  chkAll: boolean = false;
  columns: any[] = [
    { tag: 'sc', name: 'Standard.Code', value: 'Translate.StandardCode', isChecked: true },
    { tag: 'c', name: 'CertificateCode', value: 'Translate.CertificateCode', isChecked: true },
    { tag: 'sit', name: 'Standard.InstrumentType.Name', value: 'Translate.InstrumentType', isChecked: true },
    { tag: 'su', name: 'Laboratory.Name', value: 'Translate.Laboratory', isChecked: true },
    { tag: 'd', name: 'Date', value: 'Translate.Date', isChecked: true },
    { tag: 'ia', name: 'Standard.IsActive', value: 'Translate.Active', isChecked: true },
    { tag: 'ssu', name: 'Standard.Supplier.Name', value: 'Translate.Supplier', isChecked: false }
  ];
  currentPage: number = 1;
  data: any[];
  filter: string;
  hasViewCertificatePermission: boolean = false;
  isCheckingAll: boolean = false;
  isLoading: boolean = false;
  isSearching: boolean = false;
  orderBy: string = 'Standard.Code';
  overallCount: number = 1;
  pageSize: number = 25;
  qtVisibleColumns: number = this.columns.filter(c => c.isChecked).length;
  search: string = '';
  selectedItems: any = [];
  sortOrder: string = 'asc';
  subscribe: Subscription;
  totalPages: number = 1;

  constructor(
    private app: AppComponent,
    public dialog: DialogsService,
    private el: ElementRef,
    public mainLayout: LayoutMainComponent,
    private renderer2: Renderer2,
    private router: Router,
    private standardService: StandardService,
    public storedFileService: StoredFileService
  ) { }

  ngOnInit() {
    this.checkPermissions();
    this.refresh();
    this.checkColumnsPreferences();
  }

  ngOnDestroy() {
    this.subscribe.unsubscribe();
  }

  checkAll(ev) {
    this.isCheckingAll = true;
    this.data.forEach((item) => {
      item.checked = ev.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedItems = this.data.filter(item => item.checked).length;
  }

  checkColumn(event, str: string) {
    event.stopPropagation();

    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1 || this.columns.filter(c => c.isChecked).length === 1 && this.columns[idx].isChecked) {
      return;
    }

    this.columns[idx].isChecked = !this.columns[idx].isChecked;
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;

    this.saveColumnsPreferences(this.columns);
  }

  checkColumnsPreferences() {
    if (!localStorage.getItem('webview.standardList.columns')) {
      this.saveColumnsPreferences(this.columns);
    }
    const savedColumns = JSON.parse(localStorage.getItem('webview.standardList.columns'));

    this.columns.forEach(c => c.isChecked = savedColumns.findIndex(sc => sc === c.tag) !== -1);
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;
  }

  checkPermissions() {
    const contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;
    this.hasViewCertificatePermission = ContactCustomer.hasPermission(ERoleActionWebview.StandardsView, contact.RoleWebView.ActionsPermissions);
  }

  closeSidenav() {
    this.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/standards/`]);
  }

  enableSearch(el) {
    this.isSearching = !this.isSearching;
    this.search = '';
  }

  isColumnChecked(str: string): boolean {
    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return false;
    }

    return this.columns[idx].isChecked;
  }

  nextPage(e) {
    this.currentPage = e;
    this.refresh();
  }

  onSelectionChange(a) {
    if (!this.isCheckingAll) {
      this.checkedItems = this.data.filter(item => item.checked).length;
      this.selectedItems = this.data.filter(item => item.checked);

      if (this.data.filter(item => item.checked).length !== this.data.length && this.chkAll) {
        this.chkAll = false;
      }
      if (this.data.filter(item => item.checked).length === this.data.length && !this.chkAll) {
        this.chkAll = true;
      }
    }
  }

  openDetails(item: any = null) {
    if (this.cancelSidenavOpen) {
      this.cancelSidenavOpen = false;
      return;
    }

    this.router.navigate([`/${this.app.currentContact.Domain}/standards/` + item.Identifier]);
    // wherever
    this.sidenavRight.open();
  }

  prevent($event) {
    this.cancelSidenavOpen = true;
    $event.preventDefault();
  }

  previousPage(e) {
    this.currentPage = e;
    this.refresh();
  }

  refresh(search?: string, orderBy?: string, sortOrder?: string) {
    this.filter = '';
    this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
    if (search !== undefined) {
      this.search = search;
    }
    if (orderBy) {
      this.orderBy = orderBy;
    }
    if (sortOrder) {
      this.sortOrder = sortOrder;
    }
    const lstSpinner = this.el.nativeElement.querySelectorAll('mat-spinner');
    for (let x = 0; x < lstSpinner.length; x++) {
      this.renderer2.setStyle(lstSpinner[x], 'display', 'block');
    }
    this.subscribe = this.standardService.getStandardCalibrations(null, this.search, this.orderBy, this.sortOrder, this.pageSize, this.currentPage, this.filter).subscribe(data => {
      for (let x = 0; x < lstSpinner.length; x++) {
        this.renderer2.setStyle(lstSpinner[x], 'display', 'none');
      }

      this.data = Helper.deserialize(data.text());

      if (this.selectedItems) {
        this.data.forEach(d => d.checked = this.selectedItems.find(s => s.Identifier === d.Identifier && s.checked) ? true : false);
      }
      this.checkedItems = this.data.filter(d => d.checked).length;
      this.overallCount = this.data.length > 0 ? this.data[0].OverallCount : 0;
      this.totalPages = Helper.getTotalPages(this.pageSize, this.overallCount);
    });
  }

  saveColumnsPreferences(columns: any[]) {
    localStorage.setItem('webview.standardList.columns', JSON.stringify(columns.filter(c => c.isChecked).map(c2 => c2.tag)));
  }

  searchData(search) {
    this.refresh(this.search);
  }

  setPageSize(n) {
    this.pageSize = n;
    this.currentPage = 1;
    this.refresh();
  }

  sortData(data) {
    this.refresh(undefined, data.orderBy, data.sortOrder);
  }

  view() {
    this.isLoading = true;
    let identifiers = this.data.filter(d => d.checked).map(obj => obj.Identifier);
    let lstFilters = '"StandardCalibration.Identifier_in(\'' + identifiers.join('\',\'') + '\')"';
    let lstFields = '"SystemFileName","UserFileName","StandardCalibration.CertificateCode"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(value => {
        storedFiles = value.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError');
          this.isLoading = false;
        }
        this.storedFileService.download(storedFiles as StoredFile[])
          .subscribe(data => {
            this.isLoading = false;
            if (data) {
              const file = new Blob([data], { type: data.type });

              this.storedFileService.view(file, false, this.pdfViewer, `Certificado ${storedFiles[0].CertificateCode}`);

            } else {
              this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError');
            }
          }, () => {
            this.dialog.error('Message.ErrorAlert', 'Message.OpenStandardCertificateError');
            this.isLoading = false;
          });
      }, () => { this.dialog.error('Message.ErrorAlert', 'Message.OpenStandardCertificateError'); this.isLoading = false; });
  }
}



// WEBPACK FOOTER //
// ./src/app/standards/standard-calibration-list/standard-calibration-list.component.ts